import React from 'react';
import moment from 'moment-timezone';
import styles from './AlertDetailModal.module.css';
import { FloComponentProps } from 'components';
import { FloModal } from 'components';
import { useTranslation } from 'react-i18next';
import { FloModalBodyProps, StatusIndicator, FloButton, FloButtonType } from 'components';
import { Modal, Row, Col } from 'react-bootstrap';
import { Alert, Location, AlertStatus, isLocationFullAccess, isSmallDrip, getSmallDripCategory, isCriticalAlertDetect} from 'model';
import Message from 'containers/common/Message';
import { AlertFeedbackTypes } from 'model/api';

interface AlertDetailBodyProps extends FloModalBodyProps {
  alert:            Alert;
  currentLocation?: Location;
}

const AlertDetailBody: React.FC<AlertDetailBodyProps> = React.memo(({alert, currentLocation}) => {
  const { t } = useTranslation();
  const location = (alert.location as Location).address ? alert.location as Location : currentLocation;

  const getGallonsWasted = (category: number | undefined): string | undefined => {
    switch (category) {
      case 1:
        return t('50-360');
      case 2:
        return t('5-50');
      case 3:
        return t('1-5');
      case 4:
        return t('2oz-1')
    }
  }

  return (
    <Row className={styles.bodyContainer}>
      <Col xs="12">
        <Row>
          <Col xs="12">
            <Message />
          </Col>
        </Row>
        { location &&
        <Row className={styles.infoRow}>
          <Col xs="2" className={styles.rowTitle}>{t('Location')}</Col>
          <Col xs="10">  
            <a className={styles.mainText} href={`/unit/${location?.id}`} target="_blank" rel="noopener noreferrer">
              <div className={styles.locationAddress}>{location?.nickname}</div>
              <div className={styles.mainText}>{location?.address}</div>
              <div className={styles.secondaryText}>{`${location?.city ? `${location.city}, ` : ''}${location?.state ? `${location?.state.toUpperCase()}, `: '' }${location?.country.toUpperCase()}`}</div>
            </a>
          </Col>
        </Row> 
        }
        <Row className={styles.infoRow}>
          <Col xs="2" className={styles.rowTitle}>{t('Date')}</Col>
          <Col xs="10">
            <div className={styles.mainText}>{moment.utc(alert.createAt).tz(location?.timezone || 'US/Pacific').format('LLL')}</div>
          </Col>
        </Row>
        <Row className={styles.infoRow}>
          <Col xs="2" className={styles.rowTitle}>{t('Details')}</Col>
          <Col xs="10">
            <div className={styles.mainText}>{alert.displayMessage}</div>
          </Col>
        </Row>
        { isSmallDrip(alert) &&
          <Row className={styles.infoRow}>
            <Col xs="2" className={styles.rowTitle}>{t('Category')}</Col>
            <Col xs="10">
              <div className={styles.mainText}><span className={styles.category}>{getSmallDripCategory(alert)}</span><span>{`${getGallonsWasted(getSmallDripCategory(alert))} ${t('gallons_wasted')}`}</span></div>
            </Col>
          </Row>
        }
        <Row className={styles.infoRow}>
          <Col xs={{ span: 10, offset: 2 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM9 12H7V7H9V12ZM8 6C7.4 6 7 5.6 7 5C7 4.4 7.4 4 8 4C8.6 4 9 4.4 9 5C9 5.6 8.6 6 8 6Z" fill="#1B355E"/>
            </svg>
            <a className={styles.link} href={`/support/alarms/${alert.alarm.id}/learnmore`} target="_blank" rel="noopener noreferrer">
              {`${t('Learn more about')} ${alert.displayTitle.toLowerCase()}`}
            </a>
          </Col>
        </Row>
      </Col>
    </Row>
  )
}) 

export interface AlertDetailModalProps extends FloComponentProps {
  show:         boolean;
  onHide:       () => void;
  alert:        Alert | null;
  onResolve:    (alert: Alert) => void;
  onFeedbackCriticalAlert: (alert: Alert, feedbackType: string) => void;
  isSubmitting: boolean,
  location?:    Location;
}

export const AlertDetailModal: React.FC<AlertDetailModalProps> = React.memo(({onHide, onResolve, onFeedbackCriticalAlert, alert, location, ...rest}) => {
  const { t } = useTranslation();
  const loc = (alert?.location as Location)?.address ? alert?.location as Location : location;
  //const isLeakAlert = alert?.alarm && isLeakDetect(alert)
  const isCriticalAlert = alert?.alarm && isCriticalAlertDetect(alert) 

  return ( 
    <FloModal
      {...rest}
      onHide          = {onHide}
      name            = "alert-detail-modal"
      title           = {alert?.displayTitle || ''}
      className       = {styles.root}
      body            = { (props) => alert ?
        <AlertDetailBody
          {...props}
          alert           = {alert}
          currentLocation = {location}
        />
        : null
      }
      header          = { (props) =>
        <div className={styles.header}>
          { alert?.alarm.severity !== undefined && <StatusIndicator className={styles.statusIndicator} severity={alert?.alarm.severity} /> }
          <Modal.Title id={'title-header'} className={styles.modalTitle}>{alert?.displayTitle || ''}</Modal.Title>
        </div>
      }
      footer          ={ (props) => {
        if (alert?.status === AlertStatus.RESOLVED || !isLocationFullAccess(loc)) {
          return <>
            <FloButton
              text       = {t('Close')}
              className  = {styles.alertActionButton}
              type       = {FloButtonType.SECONDARY}
              onClick    = {() => props.onHide()}
            />
          </>
        } else {
          if (isCriticalAlert) {
            return <>
              <Col xs="12">
                <Row className={styles.infoRow}>
                  <Col xs="12">  
                    <div className={styles.mainText}>{t('critical-alert-feedback-question')}</div>
                  </Col>
                </Row>   
              </Col> 
              <FloButton
              text       = {t('Yes')}
              className  = {styles.alertCriticalActionButton}
              onClick    = {() => alert ? onFeedbackCriticalAlert(alert, AlertFeedbackTypes.LEAK_ALERT_NORMAL_CONFIRM) : {}}
              disabled   = {props.disabled || !props.isFormValid}
              type       = {FloButtonType.DEFAULT}
              />

              <FloButton
              text       = {t('No')}
              className  = {styles.alertCriticalActionButton}
              onClick    = {() => alert ? onFeedbackCriticalAlert(alert, AlertFeedbackTypes.LEAK_ALERT_NORMAL_DENY) : {}}
              disabled   = {props.disabled || !props.isFormValid}
              type       = {FloButtonType.DEFAULT}
              />

              <FloButton
              text       = {t('Not Sure')}
              className  = {styles.alertCriticalActionButton}
              onClick    = {() => alert ? onFeedbackCriticalAlert(alert, AlertFeedbackTypes.LEAK_ALERT_NORMAL_NOT_SURE) : {}}
              disabled   = {props.disabled || !props.isFormValid}
              type       = {FloButtonType.DEFAULT}
              /> 

              <FloButton
              text       = {t('Cancel')}
              className  = {styles.alertCriticalActionButton}
              type       = {FloButtonType.SECONDARY}
              onClick    = {() => props.onHide()}
              />
            </>
          } else {
            return <>
              <FloButton
              text       = {t('Resolve Alert')}
              className  = {styles.alertActionButton}
              onClick    = {() => alert ? onResolve(alert) : {}}
              showLoader = {props.isSubmitting}
              disabled   = {props.disabled || !props.isFormValid}
              type       = {FloButtonType.DEFAULT}
              />

              <FloButton
              text       = {t('Cancel')}
              className  = {styles.alertActionButton}
              type       = {FloButtonType.SECONDARY}
              onClick    = {() => props.onHide()}
              />
            </>
          }
        }
      }
    }
  />
  );
})